/**
 * School badges (ป้ายสำนัก) in the school panel: the four tiers earned with the school's neigong,
 * each with its title and the rank of uniform it brings, a button to claim the next one and to
 * choose which claimed rank of uniform to wear.
 */
import {
  BADGE_TIERS,
  badgeTitleId,
  badgeViews,
  getSchool,
  lookOf,
  NEIGONG,
  schoolNeigongLevel,
  TITLES,
  uniformShown,
  type PlayerCommand,
  type World,
} from '@nine/shared';
import { artH, artW, artX, artY } from '../render/pixel';
import { badgeIcon, iconEl } from '../render/icons';
import type { SpriteBank } from '../render/sprites';
import { h } from './dom';

/** A badge in its tier's metal (icons/badge), 32 px art at 2×, dim while locked. */
const plaque = (tier: number, lit: boolean): HTMLCanvasElement =>
  iconEl(badgeIcon(tier), `ico64 badge-plaque${lit ? '' : ' dim'}`);

/** The player's figure in a rank of their school uniform (no fashion over it). */
function uniformPicture(world: World, sprites: SpriteBank | undefined, tier: number): HTMLCanvasElement {
  const c = h('canvas', { class: 'badge-fig' });
  c.width = 64;
  c.height = 80;
  const set = sprites?.of({ sprite: lookOf(world.progress).sprite, outfit: tier ? [`uniform${tier}`] : [] });
  const img = set?.idle[0];
  if (set && img) {
    const g = c.getContext('2d')!;
    g.imageSmoothingEnabled = false;
    const k = 78 / set.h;
    g.drawImage(
      img,
      32 + (artX(img) - set.ax) * k,
      78 + (artY(img) - set.ay) * k,
      artW(img) * k,
      artH(img) * k,
    );
  }
  return c;
}

export function badgeSection(
  world: World,
  sprites: SpriteBank | undefined,
  command: (c: PlayerCommand) => void,
): HTMLElement | null {
  const pr = world.progress;
  if (!pr.school) return null;
  const sc = getSchool(pr.school);
  const ng = NEIGONG[sc.neigong]!;
  const level = schoolNeigongLevel(pr);
  const worn = uniformShown(pr);
  const next = BADGE_TIERS.find((b) => b.level > level);
  const btn = (label: string, cls: string, cmd: PlayerCommand | null, disabled = false) => {
    const b = h('button', { type: 'button', class: `btn ${cls}`, disabled: disabled || !cmd }, label);
    if (cmd) b.addEventListener('click', () => command(cmd));
    return b;
  };
  const plain = h(
    'section',
    { class: `badge-card plain ${worn === 0 ? 'worn' : ''}` },
    uniformPicture(world, sprites, 0),
    h(
      'div',
      { class: 'badge-body' },
      h('div', { class: 'art-name' }, 'ชุดสำนักธรรมดา'),
      h('small', {}, 'ได้รับเมื่อเข้าสำนัก'),
      worn === 0
        ? h('span', { class: 'chip on' }, 'สวมอยู่')
        : btn('สวมชุดนี้', 'ghost', { t: 'wearUniform', tier: 0 }),
    ),
  );
  return h(
    'div',
    { class: 'badge-wrap' },
    h('h4', {}, 'ป้ายสำนัก'),
    h(
      'p',
      { class: 'note' },
      `ฝึก${ng.name}ให้ถึงขั้นที่กำหนดเพื่อรับป้ายสำนัก แต่ละขั้นได้ฉายาและชุดสำนักประจำขั้น · ตอนนี้ขั้น ${level}/${ng.maxLevel}` +
        (next ? ` · อีก ${next.level - level} ขั้นถึง${next.name}` : ' · ได้ป้ายครบทุกขั้นแล้ว'),
    ),
    h(
      'div',
      { class: 'badge-grid' },
      plain,
      ...badgeViews(pr).map(({ def, status }) => {
        const title = TITLES[badgeTitleId(sc.id, def.tier)]!;
        const pct = Math.min(1, level / def.level);
        let action: HTMLElement;
        if (status === 'ready') action = btn(`รับ${def.name}`, '', { t: 'claimBadge', tier: def.tier });
        else if (status === 'locked')
          action = h(
            'span',
            { class: 'chip' },
            level >= def.level ? 'รับขั้นก่อนหน้าก่อน' : `ต้องขั้น ${def.level}`,
          );
        else
          action = h(
            'div',
            { class: 'badge-acts' },
            worn === def.tier
              ? h('span', { class: 'chip on' }, 'สวมอยู่')
              : btn('สวมชุดนี้', 'ghost', { t: 'wearUniform', tier: def.tier }),
            pr.titleWorn === title.id
              ? h('span', { class: 'chip on' }, 'ใช้ฉายาอยู่')
              : btn('ใช้ฉายา', 'ghost', { t: 'setTitle', id: title.id }),
          );
        const bar = h('div', { class: 'bar thin badge-bar' }, h('i', {}));
        (bar.firstChild as HTMLElement).style.width = `${pct * 100}%`;
        return h(
          'section',
          { class: `badge-card s-${status} ${worn === def.tier && status === 'claimed' ? 'worn' : ''}` },
          h(
            'div',
            { class: 'badge-pics' },
            plaque(def.tier, status !== 'locked'),
            uniformPicture(world, sprites, def.tier),
          ),
          h(
            'div',
            { class: 'badge-body' },
            h('div', { class: 'art-name' }, def.name),
            h('b', { class: `title-name t-${title.style}` }, title.name),
            h(
              'small',
              {},
              `${ng.name} ขั้น ${Math.min(level, def.level)}/${def.level} · ฉายา + ชุดสำนักขั้น ${def.tier}`,
            ),
            bar,
            action,
          ),
        );
      }),
    ),
  );
}
